import { Item, logEvent } from "firebase/analytics";
import { useCallback, useEffect } from "react";
import { useLocation } from "react-router";
import { analytics } from "../../firebase";

/**
 * Hook for sending page views to Google Analytics. Must be called inside of the router.
 *
 * @returns Function for manually sending a page view
 */
export function usePageTracking() {
    const location = useLocation();

    const logPageView = useCallback((pagePath: string) => {
        logEvent(analytics, "page_view", {
            page_path: pagePath,
        });
    }, []);

    useEffect(() => {
        logPageView(location.pathname);
    }, [location, logPageView]);

    return { logScreenView: logPageView };
}

/**
 * Sends a login event to Google Analytics
 * @param method the authentication provider (ex. google.com)
 */
export function loginEvent(method?: string) {
    logEvent(analytics, "login", { method });
}

/**
 * Sends an account creation event to Google Analytics
 * @param method the authentication provider (ex. google.com)
 */
export function signUpEvent(method?: string) {
    logEvent(analytics, "sign_up", { method });
}

/**
 * Sends a share event to Google Analytics
 * @param method the sharing medium/platform (ex. Facebook)
 * @param contentType the type of content (ex. study)
 * @param itemID the id of the content
 */
export function shareEvent(method: string, contentType: string, itemID: string) {
    logEvent(analytics, "share", {
        method,
        content_type: contentType,
        item_id: itemID,
    });
}

/**
 * Sends a save event to Google Analytics.
 * @param nctId the study saved by the user
 */
export function saveStudyEvent(nctId: string) {
    /*
        logEvent(analytics, "save_study", {
            items: [{ item_id: nctId }],
        });
    */
    logEvent(analytics, "add_to_cart", {
        items: [{ item_id: nctId, item_name: nctId }],
    });
}

/**
 * Sends a contact event to Google Analytics
 * @param nctId the study saved by the user
 * @param recordId the firestore document created to record the contact. prevents duplicate logging
 */
export function contactStudyEvent(nctId: string, recordId: string) {
    /*
    logEvent(analytics, "contact_study", {
        items: [{ item_id: nctId }],
        transaction_id: recordId,
    });
    */
    logEvent(analytics, "item_purchase", {
        items: [{ item_id: nctId, item_name: nctId }],
        transaction_id: recordId,
    });
}

/**
 * View a study from the list of sponsored studies.
 *
 * @param nctId
 */
export function viewSponsoredStudy(nctId: string) {
    logEvent(analytics, "view_promotion", {
        promotion_name: "Sponsored Study",
        items: [{ item_id: nctId, item_name: nctId }],
    });
}

/**
 * Logs an interaction with the study search filters.
 * @param dimension the facet of search (ex. condition)
 * @param value  the new value of the facet
 * @param method the method (if there are multiple) used to update the value (ex. autodetect vs autocomplete)
 */
export function filterUpdateEvent(dimension: string, value: string, method?: string) {
    logEvent(analytics, "filter_update", {
        dimension,
        value,
        method,
    });
}

/**
 * Logs the studies that have been shown to the user
 * @param nctIds the ids of the studies shown to the user
 */
export function viewStudiesEvent(nctIds: string[]) {
    const items: Item[] = nctIds.map((id, index) => ({
        item_id: id,
        item_name: id,
        item_index: index,
    }));
    logEvent(analytics, "view_item", { items });
}

/**
 * Logs an interaction with a study
 * @param nctId the id of the study
 * @param action the interaction type (ex. view_location_list)
 */
export function selectStudyEvent(nctId: string, action: string) {
    logEvent(analytics, "select_item", {
        action,
        items: [{ item_id: nctId }],
    });
}

/**
 * Logs an interaction with a read-aloud widget
 * @param itemId the id of the item that was read aloud
 */
export function readAloudEvent(itemId: string) {
    logEvent(analytics, "read_aloud", {
        item_id: itemId,
    });
}
